<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  useTemplateRef,
} from 'vue';
import { useMediaQuery } from '@vueuse/core';

import {
  buildMessageSchema,
  buildEditor,
  EditorView,
  MessageMarkdownTransformer,
  MessageMarkdownSerializer,
  EditorState,
  Selection,
} from '@chatwoot/prosemirror-schema';

import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { useI18n } from 'vue-i18n';
import CopilotEditorMobile from './CopilotEditorMobile.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  editorId: { type: String, default: '' },
  placeholder: {
    type: String,
    default: 'Give copilot additional prompts, or ask anything else...',
  },
  generatedContent: { type: String, default: '' },
  actionsDisabled: { type: Boolean, default: false },
  autofocus: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  'blur',
  'input',
  'update:modelValue',
  'keyup',
  'focus',
  'keydown',
  'send',
  'close',
  'accept',
  'discard',
]);

const { formatMessage } = useMessageFormatter();
const { t } = useI18n();
const jarvisThinkingLogo = '/brand-assets/jarvis-thinking-logo.png';
const isMobileViewport = useMediaQuery('(max-width: 768px)');
const isMobileAdjustmentOpen = ref(false);

// Minimal schema with no marks or nodes for copilot input
const copilotSchema = buildMessageSchema([], []);

const handleSubmit = () => emit('send');
const handleClose = () => emit('close');

const hasText = computed(() => (props.modelValue || '').trim().length > 0);
const editorPlaceholder = computed(() =>
  isMobileViewport.value
    ? t('CONVERSATION.REPLYBOX.JARVIS_ADJUSTMENT_LABEL')
    : props.placeholder
);

const createState = (
  content,
  placeholder,
  plugins = [],
  enabledMenuOptions = []
) => {
  return EditorState.create({
    doc: new MessageMarkdownTransformer(copilotSchema).parse(content),
    plugins: buildEditor({
      schema: copilotSchema,
      placeholder,
      plugins,
      enabledMenuOptions,
    }),
  });
};

// we don't need them to be reactive
// It cases weird issues where the objects are proxied
// and then the editor doesn't work as expected
let editorView = null;
let state = null;

// reactive data
const isTextSelected = ref(false); // Tracks text selection and prevents unnecessary re-renders on mouse selection

// element refs
const editor = useTemplateRef('editor');

function contentFromEditor() {
  if (editorView) {
    return MessageMarkdownSerializer.serialize(editorView.state.doc);
  }
  return '';
}

function focusEditorInputField() {
  if (!editorView) return;
  const { tr } = editorView.state;
  const selection = Selection.atEnd(tr.doc);

  editorView.dispatch(tr.setSelection(selection));
  editorView.focus();
}

function emitOnChange() {
  emit('update:modelValue', contentFromEditor());
  emit('input', contentFromEditor());
}

function onKeyup() {
  emit('keyup');
}

function onKeydown(view, event) {
  emit('keydown');

  // Handle Enter key to send message (Shift+Enter for new line)
  // Skip if IME composition is active (CJK character confirmation)
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    handleSubmit();
    return true; // Prevent ProseMirror's default Enter handling
  }

  return false; // Allow other keys to work normally
}

function onBlur() {
  emit('blur');
}

function onFocus() {
  emit('focus');
}

function checkSelection(editorState) {
  const hasSelection = editorState.selection.from !== editorState.selection.to;
  if (hasSelection === isTextSelected.value) return;
  isTextSelected.value = hasSelection;
}

// computed properties
const plugins = computed(() => {
  return [];
});

const enabledMenuOptions = computed(() => {
  return [];
});

function reloadState() {
  if (!editorView) return;
  state = createState(
    props.modelValue,
    editorPlaceholder.value,
    plugins.value,
    enabledMenuOptions.value
  );
  editorView.updateState(state);
  focusEditorInputField();
}

function createEditorView() {
  editorView = new EditorView(editor.value, {
    state: state,
    dispatchTransaction: tx => {
      state = state.apply(tx);
      editorView.updateState(state);
      if (tx.docChanged) {
        emitOnChange();
      }
      checkSelection(state);
    },
    handleDOMEvents: {
      keyup: onKeyup,
      focus: onFocus,
      blur: onBlur,
      keydown: onKeydown,
    },
  });
}

function destroyEditorView() {
  editorView?.destroy();
  editorView = null;
  state = null;
}

function mountEditorView() {
  if (!editor.value) return;
  destroyEditorView();
  state = createState(
    props.modelValue,
    editorPlaceholder.value,
    plugins.value,
    enabledMenuOptions.value
  );
  createEditorView();
  editorView.updateState(state);

  if (props.autofocus) {
    focusEditorInputField();
  }
}

async function openMobileAdjustment() {
  isMobileAdjustmentOpen.value = true;
  await nextTick();
  mountEditorView();
}

// watchers
watch(
  computed(() => props.modelValue),
  (newValue = '') => {
    if (newValue !== contentFromEditor()) {
      reloadState();
    }
  }
);

watch(
  computed(() => props.editorId),
  () => {
    reloadState();
  }
);

watch(isMobileViewport, async mobile => {
  destroyEditorView();
  isMobileAdjustmentOpen.value = false;
  if (mobile) return;
  await nextTick();
  mountEditorView();
});

// lifecycle
onMounted(() => {
  if (!isMobileViewport.value) mountEditorView();
});

onBeforeUnmount(destroyEditorView);
</script>

<template>
  <CopilotEditorMobile
    v-if="isMobileViewport"
    :generated-content="generatedContent"
    :adjustment-open="isMobileAdjustmentOpen"
    :actions-disabled="actionsDisabled"
    @close="handleClose"
    @open-adjustment="openMobileAdjustment"
    @accept="emit('accept')"
    @discard="emit('discard')"
  >
    <template #editor>
      <div class="editor-root editor--copilot">
        <div ref="editor" />
        <div v-show="hasText" class="copilot-suggestion__send-wrap">
          <button
            type="button"
            class="copilot-suggestion__send"
            :aria-label="t('CONVERSATION.REPLYBOX.SEND')"
            @click="handleSubmit"
          >
            <i class="i-ph-paper-plane-right-fill" />
          </button>
        </div>
      </div>
    </template>
  </CopilotEditorMobile>

  <div v-else class="copilot-suggestion">
    <div class="copilot-suggestion__header">
      <div class="copilot-suggestion__heading">
        <span class="copilot-suggestion__brand-icon" aria-hidden="true">
          <img
            :src="jarvisThinkingLogo"
            alt=""
            class="size-10 shrink-0 rounded-full object-cover"
          />
        </span>
        <div>
          <h3 class="copilot-suggestion__title">
            {{ t('CONVERSATION.REPLYBOX.JARVIS_SUGGESTION_TITLE') }}
          </h3>
          <p class="copilot-suggestion__description">
            {{ t('CONVERSATION.REPLYBOX.JARVIS_SUGGESTION_DESCRIPTION') }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="copilot-suggestion__close"
        :aria-label="t('GENERAL.CLOSE')"
        @click="handleClose"
      >
        <i class="i-lucide-x" />
      </button>
    </div>

    <div class="copilot-suggestion__body">
      <div class="copilot-suggestion__response">
        <span class="copilot-suggestion__response-label">
          {{ t('CONVERSATION.REPLYBOX.JARVIS_SUGGESTION_LABEL') }}
        </span>
        <div class="copilot-suggestion__scroll">
          <p
            v-dompurify-html="formatMessage(generatedContent, false)"
            v-twemoji
            class="copilot-suggestion__text"
          />
        </div>
      </div>

      <div class="copilot-suggestion__prompt">
        <span class="copilot-suggestion__prompt-label">
          {{ t('CONVERSATION.REPLYBOX.JARVIS_ADJUSTMENT_LABEL') }}
        </span>
        <div class="editor-root editor--copilot">
          <div ref="editor" />
          <div v-show="hasText" class="copilot-suggestion__send-wrap">
            <button
              type="button"
              class="copilot-suggestion__send"
              :aria-label="t('CONVERSATION.REPLYBOX.SEND')"
              @click="handleSubmit"
            >
              <i class="i-ph-paper-plane-right-fill" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@chatwoot/prosemirror-schema/src/styles/base.scss';

.copilot-suggestion {
  position: relative;
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(99, 102, 241, 0.22);
  background: linear-gradient(
    120deg,
    rgba(59, 130, 246, 0.07),
    rgba(139, 92, 246, 0.07) 60%,
    rgba(59, 130, 246, 0.07)
  );
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.copilot-suggestion__header {
  display: contents;
}

.copilot-suggestion__heading {
  display: none;
}

.copilot-suggestion__close {
  position: absolute;
  z-index: 2;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  width: 2.25rem;
  height: 2.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: #475569;
  background: rgba(15, 23, 42, 0.06);
  transition:
    color 0.15s ease,
    background 0.15s ease;
}

.copilot-suggestion__close:hover {
  color: #fff;
  background: rgba(51, 65, 85, 0.75);
}

.copilot-suggestion__close i {
  width: 1.25rem;
  height: 1.25rem;
}

.copilot-suggestion__response-label,
.copilot-suggestion__prompt-label {
  display: none;
}

.copilot-suggestion__scroll {
  margin-bottom: 0.75rem;
  max-height: 14rem;
  overflow-y: auto;
  padding-right: 2.75rem;
}

.copilot-suggestion__text {
  @apply text-n-slate-12;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.55;

  strong {
    @apply font-semibold;
  }

  a {
    color: #6366f1;
    text-decoration: underline;
  }

  code {
    background: rgba(99, 102, 241, 0.12);
    padding: 0.05rem 0.3rem;
    border-radius: 0.25rem;
    font-size: 0.85em;
  }
}

.copilot-suggestion__send {
  display: flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: #fff;
  background: #00a884;
  box-shadow: 0 2px 8px rgba(0, 168, 132, 0.2);
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.copilot-suggestion__send:hover {
  opacity: 0.9;
}

.copilot-suggestion__send:active {
  transform: scale(0.96);
}

.copilot-suggestion__send-wrap {
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
}

.editor--copilot {
  position: relative;
  @apply rounded-lg;
  border: 1px solid rgba(99, 102, 241, 0.18);
  background: rgba(255, 255, 255, 0.72);

  .ProseMirror-woot-style {
    height: 2.75rem;
    min-height: 2.75rem;
    max-height: 2.75rem !important;
    overflow: hidden;
    padding: 0.65rem 2.75rem 0.65rem 0.875rem !important;

    .empty-node {
      &::before {
        @apply text-n-slate-10;
        display: block;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }
}

@media (min-width: 769px) {
  .copilot-suggestion {
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .copilot-suggestion__header {
    display: flex;
    min-height: 4.5rem;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    background: #fff;
  }

  .copilot-suggestion__heading {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.75rem;
  }

  .copilot-suggestion__brand-icon {
    display: inline-flex;
    width: 2.5rem;
    height: 2.5rem;
    flex: 0 0 2.5rem;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 9999px;
  }

  .copilot-suggestion__title {
    margin: 0;
    color: #0f172a;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.35;
  }

  .copilot-suggestion__description {
    margin: 0.15rem 0 0;
    color: #64748b;
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .copilot-suggestion__close {
    position: static;
    width: 2rem;
    height: 2rem;
    flex: 0 0 2rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.625rem;
    color: #64748b;
    background: transparent;
    transition:
      color 0.15s ease,
      background 0.15s ease;
  }

  .copilot-suggestion__close:hover {
    color: #0f172a;
    background: #f1f5f9;
  }

  .copilot-suggestion__close i {
    width: 1.125rem;
    height: 1.125rem;
  }

  .copilot-suggestion__body {
    padding: 1rem 1.25rem 1.125rem;
    background: #fff;
  }

  .copilot-suggestion__response {
    padding: 0.875rem 1rem;
    border: 1px solid rgba(99, 102, 241, 0.14);
    border-radius: 0.75rem;
    background: linear-gradient(135deg, #f8fafc, #f5f3ff);
  }

  .copilot-suggestion__response-label,
  .copilot-suggestion__prompt-label {
    display: block;
    margin-bottom: 0.4rem;
    color: #64748b;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.045em;
    line-height: 1.3;
    text-transform: uppercase;
  }

  .copilot-suggestion__scroll {
    max-height: 9.5rem;
    margin: 0;
    padding-right: 0.25rem;
  }

  .copilot-suggestion__text {
    color: #1e293b;
    font-size: 0.875rem;
    line-height: 1.6;
  }

  .copilot-suggestion__prompt {
    margin-top: 0.875rem;
  }

  .editor--copilot {
    border-radius: 0.5rem;
    border-color: #cbd5e1;
    background: #fff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;
  }

  .editor--copilot:focus-within {
    border-color: rgba(0, 168, 132, 0.65);
    box-shadow: 0 0 0 3px rgba(0, 168, 132, 0.1);
  }

  .editor--copilot .ProseMirror-woot-style {
    height: auto;
    min-height: 5.25rem;
    max-height: 7.5rem !important;
    overflow: auto;
    padding: 0.75rem 3rem 0.75rem 0.875rem !important;
  }

  .editor--copilot .ProseMirror-woot-style .empty-node::before {
    white-space: normal;
  }

  .dark .copilot-suggestion__header,
  .dark .copilot-suggestion__body {
    background: #1f2937;
  }

  .dark .copilot-suggestion__header {
    border-color: rgba(255, 255, 255, 0.08);
  }

  .dark .copilot-suggestion__title,
  .dark .copilot-suggestion__text {
    color: #f1f5f9;
  }

  .dark .copilot-suggestion__description,
  .dark .copilot-suggestion__response-label,
  .dark .copilot-suggestion__prompt-label {
    color: #94a3b8;
  }

  .dark .copilot-suggestion__response {
    border-color: rgba(167, 139, 250, 0.2);
    background: linear-gradient(135deg, #111827, #1e1b4b);
  }

  .dark .editor--copilot {
    border-color: #475569;
    background: #111827;
  }
}
</style>
